/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { type ElementType } from 'react';
import {
	CONFIGURABLE_MENU_ID,
	GLOBAL_APP_MENU_ID,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import PlanIcon from '@atlaskit/icon-lab/core/plan';
import AppsIcon from '@atlaskit/icon/core/apps';
import AssetsGraph from '@atlaskit/icon-lab/core/assets-graph';
import DevicesIcon from '@atlaskit/icon/core/devices';
import ClockIcon from '@atlaskit/icon/core/clock';
import DashboardIcon from '@atlaskit/icon/core/dashboard';
import FilterIcon from '@atlaskit/icon/core/filter';
import GoalsIconOld from '@atlaskit/icon/core/goal';
import HeadphonesIcon from '@atlaskit/icon/core/headphones';
import PersonIcon from '@atlaskit/icon/core/person';
import {
	ConfluenceIcon,
	LoomIcon,
	TeamsIcon as NewTeamsIcon,
	ProjectsIcon as NewProjectsIcon,
	OpsgenieIcon,
	AssetsIcon,
	GoalsIcon,
} from '@atlaskit/logo';
import ProjectIcon from '@atlaskit/icon/core/project';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import PlansIcon from '@atlaskit/icon/core/roadmap';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import TeamsIcon from '@atlaskit/icon/core/teams';
import Avatar from '@atlaskit/icon/core/person-avatar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import OperationsIcon from '@atlaskit/icon/core/operations';
import SupportIcon from '@atlaskit/icon/core/support';
import { type MessageDescriptor, defineMessage } from '@atlassian/jira-intl';
import { type ConfigurableMenuId, L1_MENU_ID } from '../types.tsx';

const projects = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.projects',
	defaultMessage: 'Projects',
	description: 'Message for projects nav menu item in the sidebar',
});

const filters = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.filters',
	defaultMessage: 'Filters',
	description: 'Message for filters nav menu item in the sidebar',
});

const dashboards = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.dashboards',
	defaultMessage: 'Dashboards',
	description: 'Message for dashboards nav menu item in the sidebar',
});

const teams = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.teams',
	defaultMessage: 'Teams',
	description: 'Message for teams nav menu item in the sidebar',
});

const assets = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.assets',
	defaultMessage: 'Assets',
	description: 'Message for assets nav menu item in the sidebar',
});

const plans = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.plans',
	defaultMessage: 'Plans',
	description: 'Message for plans nav menu item in the sidebar',
});

const apps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.apps',
	defaultMessage: 'Apps',
	description: 'Message for apps nav menu item in the sidebar',
});

const operations = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.operations',
	defaultMessage: 'Operations',
	description: 'Message for Operations nav menu item in the sidebar',
});

const goals = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.goals',
	defaultMessage: 'Goals',
	description: 'Message for Goals nav menu item in the sidebar',
});

const recent = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.recent',
	defaultMessage: 'Recent',
	description: 'Message for Recent nav menu item in the sidebar',
});

const starred = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.starred',
	defaultMessage: 'Starred',
	description: 'Message for Starred nav menu item in the sidebar',
});

const roadmaps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.roadmaps',
	defaultMessage: 'Roadmaps',
	description: 'Message for Roadmaps nav menu item in the sidebar',
});

const customerHubs = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.customer-hubs',
	defaultMessage: 'Customer experiences',
	description: 'Message for Customer experiences nav menu item in the sidebar',
});

const customerServiceAnalytics = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.customerServiceAnalytics',
	defaultMessage: 'Customer service overview',
	description: 'Message for Customer service overview nav menu item in the sidebar',
});

const customerConversationsAnalytics = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.customerConversationsAnalytics',
	defaultMessage: 'Customer conversations analytics',
	description: 'Message for Customer conversations analytics nav menu item in the sidebar',
});

const customers = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.customer-s',
	defaultMessage: 'Customers',
	description: 'Message for Customers menu item in the sidebar',
});

const forYou = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.forYou',
	defaultMessage: 'For you',
	description: 'Message for For You nav menu item in the sidebar',
});

const confluence = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.confluence',
	defaultMessage: 'Confluence',
	description: 'Message for Confluence nav menu item in the sidebar',
});

const loom = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.loom',
	defaultMessage: 'Loom',
	description: 'Message for Loom nav menu item in the sidebar',
});

const opsgenie = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.opsgenie',
	defaultMessage: 'Opsgenie',
	description: 'Message for Opsgenie nav menu item in the sidebar',
});

const projectsGalaxia = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.projects-galaxia',
	defaultMessage: 'Spaces',
	description: 'Message for projects nav menu item in the sidebar',
});

const itamNonFinal = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.itam-non-final',
	defaultMessage: 'Hardware',
	description: 'Message for IT asset management nav menu item in the sidebar',
});

const helpCenterManagement = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.common.help-center-management',
	defaultMessage: 'Help centers',
	description: 'Message for Help center management nav menu item in the sidebar',
});

export const PACKAGE_NAME = 'jiraNavigationAppsSidebarNav4Common';
export const TEAM_NAME = 'jira-cosmos';

export const CONFIGURABLE_MENU_IDS: (
	| 'jira.sidebar.globalApp.goals'
	| 'jira.sidebar.globalApp.assets'
	| 'jira.sidebar.globalApp.confluence'
	| 'jira.sidebar.globalApp.loom'
	| 'jira.sidebar.globalApp.projects'
	| 'jira.sidebar.globalApp.teams'
	| 'jira.sidebar.globalApp.opsgenie'
	| 'jira.sidebar.apps'
	| 'jira.sidebar.assets'
	| 'jira.sidebar.dashboards'
	| 'jira.sidebar.filters'
	| 'jira.sidebar.goals'
	| 'jira.sidebar.operations'
	| 'jira.sidebar.plans'
	| 'jira.sidebar.projects'
	| 'jira.sidebar.recent'
	| 'jira.sidebar.starred'
	| 'jira.sidebar.teams'
	| 'jira.sidebar.roadmaps'
	| 'jira.sidebar.customerService'
	| 'jira.sidebar.customerServiceAnalytics'
	| 'jira.sidebar.customerConversationsAnalytics'
	| 'jira.sidebar.customers'
	| 'jira.sidebar.itam'
	| 'jira.sidebar.helpCenterManagement'
)[] = Object.values(CONFIGURABLE_MENU_ID);
export const CONFIGURABLE_MENU_IDS_AS_STRINGS: string[] = CONFIGURABLE_MENU_IDS;

export const GLOBAL_APP_MENU_IDS: (
	| 'jira.sidebar.globalApp.goals'
	| 'jira.sidebar.globalApp.assets'
	| 'jira.sidebar.globalApp.confluence'
	| 'jira.sidebar.globalApp.loom'
	| 'jira.sidebar.globalApp.projects'
	| 'jira.sidebar.globalApp.teams'
	| 'jira.sidebar.globalApp.opsgenie'
)[] = Object.values(GLOBAL_APP_MENU_ID);
export const GLOBAL_APP_MENU_IDS_AS_STRINGS: string[] = GLOBAL_APP_MENU_IDS;

export const CONFIGURABLE_MESSAGE_AND_ICON_MAP_WITHOUT_PROJECT_TERM: {
	[K in ConfigurableMenuId]: { message: MessageDescriptor; Icon: ElementType };
} = {
	[L1_MENU_ID.APPS]: { message: apps, Icon: AppsIcon },
	[L1_MENU_ID.GOALS]: { message: goals, Icon: GoalsIconOld },
	[L1_MENU_ID.ROADMAPS]: { message: roadmaps, Icon: PlanIcon },
	[L1_MENU_ID.PLANS]: { message: plans, Icon: PlansIcon },
	[L1_MENU_ID.PROJECTS]: { message: projectsGalaxia, Icon: ProjectIcon },
	[L1_MENU_ID.FILTERS]: { message: filters, Icon: FilterIcon },
	[L1_MENU_ID.DASHBOARDS]: { message: dashboards, Icon: DashboardIcon },
	[L1_MENU_ID.OPERATIONS]: { message: operations, Icon: OperationsIcon },
	[L1_MENU_ID.ASSETS]: { message: assets, Icon: AssetsGraph },
	[L1_MENU_ID.TEAMS]: { message: teams, Icon: TeamsIcon },
	[L1_MENU_ID.RECENT]: { message: recent, Icon: ClockIcon },
	[L1_MENU_ID.STARRED]: { message: starred, Icon: StarUnstarredIcon },
	[L1_MENU_ID.CUSTOMER_SERVICE]: {
		message: customerHubs,
		Icon: HeadphonesIcon,
	},
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: customerServiceAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMER_CONVERSATIONS_ANALYTICS]: {
		message: customerConversationsAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMERS]: {
		message: customers,
		Icon: PersonIcon,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: {
		message: assets,
		Icon: (props) => <AssetsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_GOALS]: {
		message: goals,
		Icon: (props) => <GoalsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	// App Shortcuts for Platform Component
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: {
		message: confluence,
		Icon: (props) => <ConfluenceIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_LOOM]: {
		message: loom,
		Icon: (props) => <LoomIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: {
		message: projects,
		Icon: (props) => <NewProjectsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: {
		message: teams,
		Icon: (props) => <NewTeamsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: {
		message: opsgenie,
		Icon: (props) => <OpsgenieIcon {...props} shouldUseNewLogoDesign size="small" />,
	},
	[L1_MENU_ID.ITAM]: { message: itamNonFinal, Icon: DevicesIcon },
	[L1_MENU_ID.HELP_CENTER_MANAGEMENT]: {
		message: helpCenterManagement,
		Icon: SupportIcon,
	},
};

export const FOR_YOU_MESSAGE_AND_ICON_MAP: { message: MessageDescriptor; Icon: ElementType } = {
	message: forYou,
	Icon: Avatar,
};

export const CONFIGURABLE_MESSAGE_AND_ICON_MAP_WITH_PROJECT_TERM: {
	[K in ConfigurableMenuId]: { message: MessageDescriptor; Icon: ElementType };
} = {
	[L1_MENU_ID.APPS]: { message: apps, Icon: AppsIcon },
	[L1_MENU_ID.GOALS]: { message: goals, Icon: GoalsIconOld },
	[L1_MENU_ID.ROADMAPS]: { message: roadmaps, Icon: PlanIcon },
	[L1_MENU_ID.PLANS]: { message: plans, Icon: PlansIcon },
	[L1_MENU_ID.PROJECTS]: { message: projects, Icon: ProjectIcon },
	[L1_MENU_ID.FILTERS]: { message: filters, Icon: FilterIcon },
	[L1_MENU_ID.DASHBOARDS]: { message: dashboards, Icon: DashboardIcon },
	[L1_MENU_ID.OPERATIONS]: { message: operations, Icon: OperationsIcon },
	[L1_MENU_ID.ASSETS]: { message: assets, Icon: AssetsGraph },
	[L1_MENU_ID.TEAMS]: { message: teams, Icon: TeamsIcon },
	[L1_MENU_ID.RECENT]: { message: recent, Icon: ClockIcon },
	[L1_MENU_ID.STARRED]: { message: starred, Icon: StarUnstarredIcon },
	[L1_MENU_ID.CUSTOMER_SERVICE]: {
		message: customerHubs,
		Icon: HeadphonesIcon,
	},
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: {
		message: customerServiceAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMER_CONVERSATIONS_ANALYTICS]: {
		message: customerConversationsAnalytics,
		Icon: ChartTrendUpIcon,
	},
	[L1_MENU_ID.CUSTOMERS]: {
		message: customers,
		Icon: PersonIcon,
	},
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: {
		message: assets,
		Icon: (props) => <AssetsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_GOALS]: {
		message: goals,
		Icon: (props) => <GoalsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	// App Shortcuts for Platform Component
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: {
		message: confluence,
		Icon: (props) => <ConfluenceIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_LOOM]: {
		message: loom,
		Icon: (props) => <LoomIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: {
		message: projects,
		Icon: (props) => <NewProjectsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: {
		message: teams,
		Icon: (props) => <NewTeamsIcon {...props} shouldUseNewLogoDesign size="xsmall" />,
	},
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: {
		message: opsgenie,
		Icon: (props) => <OpsgenieIcon {...props} shouldUseNewLogoDesign size="small" />,
	},
	[L1_MENU_ID.ITAM]: { message: itamNonFinal, Icon: DevicesIcon },
	[L1_MENU_ID.HELP_CENTER_MANAGEMENT]: {
		message: helpCenterManagement,
		Icon: SupportIcon,
	},
};

export const SPACES_MESSAGE_AND_ICON_MAP_WITH_PROJECT_TERM: {
	message: MessageDescriptor;
	Icon: ElementType;
} = {
	message: projects,
	Icon: SpacesIcon,
};

export const SPACES_MESSAGE_AND_ICON_MAP_WITHOUT_PROJECT_TERM: {
	message: MessageDescriptor;
	Icon: ElementType;
} = {
	message: projectsGalaxia,
	Icon: SpacesIcon,
};

/**
 * To be used in examples, for example
 */
export const LEFT_SIDEBAR_EXPANDED_MIN_WIDTH = 240; // in pixels
export const LEFT_SIDEBAR_EXPANDED_DEFAULT_WIDTH = 320; // in pixels
export const IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT = false;
export const TMP = 'TEAM_MANAGED_PROJECT';
export const CMP = 'COMPANY_MANAGED_PROJECT';

// used in jsw-expandable-project and board-create
export const NO_OF_BOARDS_TO_SHOW = 25;

export const FOR_YOU_MESSAGE_STRING: MessageDescriptor = forYou;

export const CONFIGURABLE_MESSAGE_STRING_WITHOUT_PROJECT_TERM: {
	[K in ConfigurableMenuId]: MessageDescriptor;
} = {
	[L1_MENU_ID.APPS]: apps,
	[L1_MENU_ID.GOALS]: goals,
	[L1_MENU_ID.ROADMAPS]: roadmaps,
	[L1_MENU_ID.PLANS]: plans,
	[L1_MENU_ID.PROJECTS]: projectsGalaxia,
	[L1_MENU_ID.FILTERS]: filters,
	[L1_MENU_ID.DASHBOARDS]: dashboards,
	[L1_MENU_ID.OPERATIONS]: operations,
	[L1_MENU_ID.ASSETS]: assets,
	[L1_MENU_ID.TEAMS]: teams,
	[L1_MENU_ID.RECENT]: recent,
	[L1_MENU_ID.STARRED]: starred,
	[L1_MENU_ID.CUSTOMER_SERVICE]: customerHubs,
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: customerServiceAnalytics,
	[L1_MENU_ID.CUSTOMER_CONVERSATIONS_ANALYTICS]: customerConversationsAnalytics,
	[L1_MENU_ID.CUSTOMERS]: customers,
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: assets,
	[L1_MENU_ID.GLOBAL_APP_GOALS]: goals,
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence,
	[L1_MENU_ID.GLOBAL_APP_LOOM]: loom,
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projects,
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: teams,
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie,
	[L1_MENU_ID.ITAM]: itamNonFinal,
	[L1_MENU_ID.HELP_CENTER_MANAGEMENT]: helpCenterManagement,
};

export const CONFIGURABLE_MESSAGE_STRING_WITH_PROJECT_TERM: {
	[K in ConfigurableMenuId]: MessageDescriptor;
} = {
	[L1_MENU_ID.APPS]: apps,
	[L1_MENU_ID.GOALS]: goals,
	[L1_MENU_ID.ROADMAPS]: roadmaps,
	[L1_MENU_ID.PLANS]: plans,
	[L1_MENU_ID.PROJECTS]: projects,
	[L1_MENU_ID.FILTERS]: filters,
	[L1_MENU_ID.DASHBOARDS]: dashboards,
	[L1_MENU_ID.OPERATIONS]: operations,
	[L1_MENU_ID.ASSETS]: assets,
	[L1_MENU_ID.TEAMS]: teams,
	[L1_MENU_ID.RECENT]: recent,
	[L1_MENU_ID.STARRED]: starred,
	[L1_MENU_ID.CUSTOMER_SERVICE]: customerHubs,
	[L1_MENU_ID.CUSTOMER_SERVICE_ANALYTICS]: customerServiceAnalytics,
	[L1_MENU_ID.CUSTOMER_CONVERSATIONS_ANALYTICS]: customerConversationsAnalytics,
	[L1_MENU_ID.CUSTOMERS]: customers,
	[L1_MENU_ID.GLOBAL_APP_ASSETS]: assets,
	[L1_MENU_ID.GLOBAL_APP_GOALS]: goals,
	[L1_MENU_ID.GLOBAL_APP_CONFLUENCE]: confluence,
	[L1_MENU_ID.GLOBAL_APP_LOOM]: loom,
	[L1_MENU_ID.GLOBAL_APP_PROJECTS]: projects,
	[L1_MENU_ID.GLOBAL_APP_TEAMS]: teams,
	[L1_MENU_ID.GLOBAL_APP_OPSGENIE]: opsgenie,
	[L1_MENU_ID.ITAM]: itamNonFinal,
	[L1_MENU_ID.HELP_CENTER_MANAGEMENT]: helpCenterManagement,
};
